import { ITINERARY_PANEL } from "../../../config/itineraryPanel.configuration";
import { defineEphemeralSlice } from "../runtime/defineEphemeralSlice";
import type { ItineraryPanelState } from "../types";

/** Default planner panel state: expanded tier, idle (no drag). */
export const ITINERARY_PANEL_DEFAULT: ItineraryPanelState = {
    "loadingItinerary":                   false,
    "loadingPricing":                     false,
    "itineraryListHeight":                ITINERARY_PANEL.panelMaxHeightVh,
    "panelSnapTier":                      "expanded",
    "rememberedItineraryListSnapTier":    "expanded",
    "rememberedItineraryDetailsSnapTier": "expanded",
    "dragHeightVh":                       null,
    "activeResizeProfileId":              null,
    "isDragging":                         false,
};

/** Itinerary list panel loaders, height, snap tier, and drag state. */
export const itineraryPanelStateSlice = defineEphemeralSlice<ItineraryPanelState>( {
    "key":     "itineraryPanelState",
    "default": () => ITINERARY_PANEL_DEFAULT,
} );

/**
 * Resolves the panel height (vh) that should currently be rendered.
 *
 * @remarks
 * While dragging, {@link ItineraryPanelState.dragHeightVh} takes precedence.
 * When idle, `maximized` resolves dynamically from `maximizedPanelHeightVh`;
 * other tiers use the committed {@link ItineraryPanelState.itineraryListHeight}.
 *
 * @param panel - Current itinerary panel ephemeral state.
 * @param maximizedPanelHeightVh - Dynamic maximized anchor (vh).
 * @returns Effective panel height in vh units.
 */
export const resolveEffectivePanelHeightVh = (
    panel: ItineraryPanelState,
    maximizedPanelHeightVh: number,
): number => {
    if ( panel.dragHeightVh !== null ) {
        return panel.dragHeightVh;
    }

    if ( panel.panelSnapTier === "maximized" ) {
        return maximizedPanelHeightVh;
    }

    return panel.itineraryListHeight;
};
